import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import api from "../api";

/**
 * Cross-app sign-in from tution-frontend mobile login.
 * Hash: #token=...&d=BASE64URL(userData JSON)&mcp=0|1&next=/student/...
 */
function decodeBase64UrlToString(s) {
  if (!s) return null;
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + pad;
  try {
    return decodeURIComponent(escape(atob(b64)));
  } catch {
    return null;
  }
}

function sanitizeStudentSsoNextPath(raw) {
  if (!raw || typeof raw !== "string") return null;
  let p = raw.trim();
  if (!p.startsWith("/")) p = `/${p}`;
  if (!/^\/student(\/|$)/i.test(p)) return null;
  if (p.includes("//")) return null;
  const lower = p.toLowerCase();
  if (lower.includes("://") || lower.includes("@")) return null;
  return p;
}

/** Role name out of a handed-over userData payload ("Student", { name: "Parent" }, …). */
function roleNameFromUserData(parsed) {
  const raw = parsed?.role;
  const name = raw && typeof raw === "object" ? raw.name : raw;
  return String(name || "").toLowerCase().trim();
}

const StudentSso = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) {
      navigate("/login", { replace: true });
      return;
    }

    let params;
    try {
      params = new URLSearchParams(hash);
    } catch {
      navigate("/login", { replace: true });
      return;
    }

    const token = params.get("token");
    if (!token || typeof token !== "string" || token.length < 10) {
      navigate("/login", { replace: true });
      return;
    }

    const d = params.get("d");
    const userJson = d ? decodeBase64UrlToString(d) : null;
    let parsed = null;
    if (userJson) {
      try {
        parsed = JSON.parse(userJson);
      } catch {
        /* ignore invalid JSON */
      }
    }

    const mcp = params.get("mcp");
    const nextPath = sanitizeStudentSsoNextPath(params.get("next"));

    /** Write the session the student portal runs on, then land on `next`. */
    const enterPortal = (accessToken, json, user, mustChangePassword) => {
      localStorage.setItem("token", accessToken);
      localStorage.setItem("role", "student");
      localStorage.removeItem("admin-token");
      localStorage.removeItem("parent-permissions");
      localStorage.removeItem("tester-permissions");
      // Fresh SSO — don't reuse a stale paywall cache from a previous session.
      localStorage.removeItem("student-subscription-lock");

      if (json) {
        localStorage.setItem("userData", json);
      }
      // The sidebar is RBAC-gated, and an absent list falls open. Only write a list that
      // actually came from a student account — a parent's permissions carry no
      // student-portal-* names and would collapse the sidebar to Dashboard alone.
      if (Array.isArray(user?.permissions)) {
        localStorage.setItem("student-permissions", JSON.stringify(user.permissions));
      } else {
        localStorage.removeItem("student-permissions");
      }
      if (user?.is_allow_mobile) {
        localStorage.setItem("student-use-mobile-dashboard", "1");
      }
      if (mustChangePassword === "1" || mustChangePassword === "0") {
        localStorage.setItem("mustChangePassword", mustChangePassword);
      }

      try {
        Object.keys(sessionStorage)
          .filter((k) => k === "exam_mode_cache" || k.startsWith("exam_mode_cache_"))
          .forEach((k) => sessionStorage.removeItem(k));
      } catch {
        /* ignore */
      }

      window.location.replace(nextPath || "/student/");
    };

    // A parent signed in on the public site hands over a parent token. Stamping it "student"
    // renders the portal against an account with no student-portal-* permissions, so exchange
    // it for the linked student session first — the same swap the parent panel's switch uses.
    if (roleNameFromUserData(parsed) === "parent") {
      localStorage.setItem("token", token);
      localStorage.setItem("role", "parent");
      api
        .post("parent/switch-to-student")
        .then((res) => {
          const data = res.data?.data;
          if (!data?.access_token) {
            throw new Error("No student session returned.");
          }
          enterPortal(
            data.access_token,
            JSON.stringify(data),
            data,
            data.must_change_password ? "1" : "0"
          );
        })
        .catch(() => {
          // No linked student account (or the swap was refused) — the parent panel is where
          // this session belongs; dumping them in a Dashboard-only student portal is not.
          window.location.replace("/parent");
        });
      return;
    }

    enterPortal(token, userJson, parsed, mcp);
  }, [navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Signing you in…
      </Typography>
    </Box>
  );
};

export default StudentSso;
